import type { BaseSyntheticEvent } from 'react';
import { useState, useEffect } from 'react';
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { toast } from 'sonner';
import { useCreateCandidate } from '../hooks/useCreateCandidate';
import { useUpdateCandidate } from '../hooks/useUpdateCandidate';
import type { Candidate } from '../types/candidate.types';
import type { CandidateFormData } from './CandidateFormFields';
import {
  DEFAULT_VALUES,
  toFormValues,
  buildPayload,
  getLabels,
  resetFormState,
} from './CandidateFormDialog.utils';

export function useCandidateFormHandlers(onOpenChange: (open: boolean) => void): {
  isLoading: boolean;
  handleClose: () => void;
  createCandidate: ReturnType<typeof useCreateCandidate>['createCandidate'];
  updateCandidate: ReturnType<typeof useUpdateCandidate>['updateCandidate'];
  onCreateSuccess: (reset: () => void, setCountry: (v: string | null) => void) => void;
  onEditSuccess: () => void;
} {
  const { createCandidate, isLoading: isCreating } = useCreateCandidate();
  const { updateCandidate, isLoading: isUpdating } = useUpdateCandidate();

  return {
    isLoading: isCreating || isUpdating,
    handleClose: () => onOpenChange(false),
    createCandidate,
    updateCandidate,
    onCreateSuccess: (reset: () => void, setCountry: (v: string | null) => void): void => {
      toast.success('Candidate created successfully');
      reset();
      setCountry(null);
      onOpenChange(false);
    },
    onEditSuccess: (): void => {
      toast.success('Candidate updated successfully');
      onOpenChange(false);
    },
  };
}

interface CandidateFormStateParams {
  mode: 'create' | 'edit';
  candidate?: Candidate | null;
  onOpenChange: (open: boolean) => void;
}

interface CandidateFormState {
  register: UseFormRegister<CandidateFormData>;
  errors: FieldErrors<CandidateFormData>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  country: string | null;
  setCountry: (country: string | null) => void;
  title: string;
  submitLabel: string;
  isLoading: boolean;
  handleClose: () => void;
}

export function useCandidateFormState({
  mode,
  candidate,
  onOpenChange,
}: CandidateFormStateParams): CandidateFormState {
  const handlers = useCandidateFormHandlers(onOpenChange);
  const [country, setCountry] = useState<string | null>(candidate?.country ?? null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CandidateFormData>({
    defaultValues: mode === 'edit' && candidate ? toFormValues(candidate) : DEFAULT_VALUES,
  });

  useEffect(() => resetFormState(reset, setCountry, mode, candidate), [candidate, mode, reset]);

  const onSubmit = handleSubmit((data) => {
    const payload = buildPayload(data, country);
    if (mode === 'create') {
      handlers.createCandidate(payload, {
        onSuccess: () => handlers.onCreateSuccess(reset, setCountry),
        onError: () => toast.error('Failed to create candidate'),
      });
    } else if (mode === 'edit' && candidate) {
      handlers.updateCandidate(
        { id: candidate.id, data: payload },
        {
          onSuccess: handlers.onEditSuccess,
          onError: () => toast.error('Failed to update candidate'),
        }
      );
    }
  });

  const { title, submitLabel } = getLabels(mode, handlers.isLoading);

  return {
    register,
    errors,
    onSubmit,
    country,
    setCountry,
    title,
    submitLabel,
    isLoading: handlers.isLoading,
    handleClose: handlers.handleClose,
  };
}
